import Grid from '@mui/material/GridLegacy';
import type { SxProps, Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useCallback, type ReactNode } from 'react';

import type { WDSComponentProps } from '../../types';
import { useTrace } from '../../utils/trace';

export interface EmptyStateProps extends WDSComponentProps {
    title: string;
    message: string;
    image?: ReactNode;
    children?: ReactNode;
    variant?: 'xsmall' | 'small' | 'large';
    messageSx?: SxProps<Theme>;
    imageSx?: SxProps<Theme>;
    bodySx?: SxProps<Theme>;
    titleSx?: SxProps<Theme>;
}

export type Sizes = {
    [K in Exclude<EmptyStateProps['variant'], undefined>]: number;
};

/**
 * A simple component to display a message when there is no data to show.
 *
 * It takes a title, message, and optional image and children, displaying them in a centered grid.
 *
 * [Documentation](https://wds2docs.dev-sqnt.com/?path=/docs/components-emptystate)
 *
 * ### Example
 * ```tsx
 * <EmptyState
 *     title="Failed to load data"
 *     message="Please try again later"
 *     sx={{ background: 'purple' }}
 *     titleSx={{ color: 'red' }}
 *     imageSx={{ background: 'yellow' }}
 * />
 * ```
 */
export default function EmptyState({
    title,
    message,
    image,
    variant = 'large',
    children,
    'automation-id': automationId,
    sx,
    messageSx,
    imageSx,
    bodySx,
    titleSx,
}: Readonly<EmptyStateProps>) {
    const getFullTrace = useTrace(automationId ?? 'empty-state');

    const variantSizes = useCallback((sizes: Sizes) => sizes[variant], [variant]);

    return (
        <Grid
            container
            justifyContent="center"
            alignItems="center"
            sx={{
                textAlign: 'center' as const,
                ...sx,
            }}
            automation-id={getFullTrace()}
        >
            {image && (
                <Grid
                    item
                    xs={12}
                    sx={{
                        maxWidth: '100%',
                        paddingLeft: '40px',
                        ...(variant === 'xsmall' && {
                            paddingLeft: '20px',
                            paddingRight: '20px',
                        }),
                        ...imageSx,
                    }}
                    automation-id={getFullTrace('image')}
                >
                    {image}
                </Grid>
            )}
            <Grid
                item
                xs={12}
                sm={variantSizes({ xsmall: 10, small: 10, large: 6 })}
                md={variantSizes({ xsmall: 10, small: 10, large: 4 })}
                sx={bodySx}
                automation-id={getFullTrace('body')}
            >
                <Typography
                    sx={{
                        fontSize: variantSizes({ xsmall: 14, small: 15, large: 20 }),
                        ...(variant === 'xsmall' && {
                            fontWeight: 'normal',
                            paddingTop: 1,
                        }),
                        ...titleSx,
                    }}
                    variant="h5"
                    automation-id={getFullTrace('title')}
                >
                    {title}
                </Typography>
                <Typography
                    sx={{
                        padding: (theme) => theme.spacing(3, 0),
                        fontSize: variantSizes({ xsmall: 12, small: 15, large: 15 }),
                        ...(variant === 'xsmall' && {
                            color: (theme) => theme.palette.text.secondary,
                            paddingTop: 0,
                            paddingBottom: 2,
                        }),
                        ...messageSx,
                    }}
                    variant="body1"
                    automation-id={getFullTrace('message')}
                >
                    {message}
                </Typography>
                {children && <Grid automation-id={getFullTrace('children')}>{children}</Grid>}
            </Grid>
        </Grid>
    );
}
